// 문항 이미지는 문제·해설 TeX의 <img> 링크가 기준이다. 이미지 요청(메모)은 링크 주소로 붙는다.
export type TaskImageRequestValue = {
  file: { key: string; size: number; objectUrl?: string | null };
  description: string;
};

export type TaskImageLink = {
  src: string;
  section: 'problem' | 'solution';
  line: number;
};

const IMAGE_TAG_REGEX = /<img[^>]*?src=(['"])(.*?)\1[^>]*?\/?>/g;

export const getImageKeyFromSrc = (src: string) => {
  const [, key] = src.split('/cms/');
  return decodeURIComponent(key ?? src.split('/').pop() ?? src);
};

export const getTaskImageLinks = (problem: string, solution: string): TaskImageLink[] => {
  const links: TaskImageLink[] = [];
  const seen = new Set<string>();
  (
    [
      ['problem', problem],
      ['solution', solution],
    ] as const
  ).forEach(([section, text]) => {
    const regex = new RegExp(IMAGE_TAG_REGEX.source, 'g');
    let match = regex.exec(text);
    while (match) {
      const src = match[2];
      if (!seen.has(src)) {
        seen.add(src);
        // 편집기 줄 번호와 맞춘다
        const line = text.slice(0, match.index).split('\n').length;
        links.push({ src, section, line });
      }
      match = regex.exec(text);
    }
  });
  return links;
};

export const formatTaskImageLinkLocation = ({ section, line }: TaskImageLink) =>
  `${section === 'problem' ? '문제' : '해설'} ${line}번 줄`;

// 서버에 있던 이미지 요청(링크를 지운 것 포함)을 파일 키별로 모은다. 같은 링크를 다시 넣으면 메모가 다시 붙는다
// (서버가 돌려주는 주소는 환경마다 CDN 도메인이 달라 TeX 링크와 다를 수 있어 키로 찾는다)
export const createImageRequestMemo = (
  imageRequests: readonly { image: { key: string; size: number; objectUrl: string }; description?: string | null }[],
) =>
  new Map<string, TaskImageRequestValue>(
    imageRequests.map(({ image, description }) => [
      image.key,
      { file: { key: image.key, size: image.size, objectUrl: image.objectUrl }, description: description || '' },
    ]),
  );

// 링크 순서대로 이미지 요청을 맞춘다. 지금 고친 메모도 memo에 남겨 두고, 링크가 없으면 목록에서만 빠진다
export const syncImageRequestsWithLinks = (
  problem: string,
  solution: string,
  current: readonly TaskImageRequestValue[],
  memo: Map<string, TaskImageRequestValue>,
): TaskImageRequestValue[] => {
  current.forEach((imageRequest) => memo.set(imageRequest.file.key, imageRequest));
  return getTaskImageLinks(problem, solution).map(({ src }) => {
    const key = getImageKeyFromSrc(src);
    const saved = memo.get(key);
    // 목록의 주소는 TeX에 실제로 있는 링크를 쓴다(교체할 때 이 주소로 링크를 바꾼다)
    return saved
      ? { ...saved, file: { ...saved.file, objectUrl: src } }
      : { file: { key, size: 0, objectUrl: src }, description: '' };
  });
};

export const removeImageTag = (text: string, src: string) =>
  text.replace(new RegExp(`${IMAGE_TAG_REGEX.source}[\\n\\r]?`, 'g'), (tag, _quote, tagSrc) =>
    tagSrc === src ? '' : tag,
  );
